import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

/**
 * Builds the localStorage key for a given content ID by appending the
 * content ID to the registered storage key.
 */
const buildKey = (contentId: string): string => {
	return `${keys.PAGE_CREATED_FROM_COPY}.${contentId}`;
};

/**
 * Records that a page was just created via a copy/duplicate operation.
 *
 * Call this immediately after the copy long-running task succeeds and the
 * destination content ID is known — i.e. right before navigating to the new
 * page in `CopyPageTreeLongTaskManager` or the modernised `CopyPageDialog`.
 *
 * @param contentId - The content ID of the **newly created** (destination) page.
 */
export const setRecentlyCopiedContentId = (contentId: string): void => {
	const key = buildKey(contentId);
	localStorage.setItem(key, 'true');
};

/**
 * Checks whether the given content ID was created from a copy/duplicate
 * operation. The localStorage entry persists until explicitly cleared via
 * {@link clearRecentlyCopiedContentId}, which should be called when the user
 * takes a subsequent action on the page (e.g. editing content, publishing,
 * navigating away).
 *
 * @param contentId - The content ID of the page being loaded.
 * @returns `true` if the content ID was created from a copy, `false` otherwise.
 */
export const isRecentlyCopiedContentId = (contentId: string): boolean => {
	const key = buildKey(contentId);
	return localStorage.getItem(key) === 'true';
};

/**
 * Clears the localStorage entry that marks a content ID as recently copied.
 *
 * Call this when the user takes a meaningful action on the copied page —
 * such as making an edit, publishing, or navigating away — to signal that
 * the page should no longer be treated as a fresh copy.
 *
 * @param contentId - The content ID whose copy marker should be removed.
 */
export const clearRecentlyCopiedContentId = (contentId: string): void => {
	const key = buildKey(contentId);
	localStorage.removeItem(key);
};
